import type { ReaperBatchLimits } from './internal-reaper-limits.ts';

/**
 * Retention for `request_lifecycle_event_batches` (#2243 / #2443 / #2653):
 * the gateway route `POST /internal/lifecycle-events/cleanup` and the
 * control-loops handler that drives it share this module, so the batch shape
 * is one number on both ends.
 *
 * The table is a per-request phase timeline read by the ops request-detail
 * page (by request_id), by the patrol (≤24h windows) and by nothing in the
 * billing chain: `usage_events → usage_rated_events → revenue_ledger` never
 * touches it. It is region-local (excluded from replication), so jp and cn
 * each reap their own copy. Deleting a row here can never change what a
 * customer was charged — which is exactly why age alone is a sufficient
 * predicate, unlike `usage_events` where the clock starts on the RATED sibling.
 */

/**
 * Rows older than this are reapable. Measured on the event's own `created_at`.
 * Default for the `LIFECYCLE_EVENTS_RETENTION_DAYS` process-env knob (#2560);
 * an invalid value fails the process at boot rather than silently falling back.
 */
export const LIFECYCLE_EVENTS_RETENTION_DAYS = 10;

/**
 * Rows removed per DELETE statement. Small on purpose: each batch is one
 * DELETE in its own short transaction that walks
 * `idx_request_lifecycle_event_batches_created_at` from the oldest end and
 * touches ~5k heap pages plus the table's indexes, so no single statement
 * holds row locks or dirties pages for long on the database that also serves
 * auth.
 *
 * Default for the gateway knob of the same name (#2681), as are the fuse
 * and budget below; ceilings and their derivation: `reaper-limits.mjs`.
 */
export const LIFECYCLE_EVENTS_CLEANUP_BATCH_ROWS = 5_000;

/**
 * Fuse on DELETE statements per route call — not a traffic quota. 8 × 5 000
 * is 40k/tick × 144 = 5.76 M/day only if every batch fills AND finishes
 * inside the 1.5s budget. The budget is what actually stops the pass; a
 * short batch means the 10-day window is met. If the budget trips, overdue
 * rows wait for the next tick (true drain, not a percentage). Do not raise
 * this to 40: more batches would only matter if they still fit in 1.5s, and
 * a slow tick must abort rather than keep deleting on the database that
 * serves auth.
 */
export const LIFECYCLE_EVENTS_CLEANUP_MAX_BATCHES = 8;

/**
 * Wall-clock budget for one route call, checked BEFORE each batch. This is
 * the stutter fuse: a slow tick aborts rather than keep deleting. jp + cn
 * serial + HTTP must stay far under the control-loops 25 s kill. Worst case
 * is one batch admitted at the last moment and running to statement_timeout
 * (~1.5 s + 5 s per region).
 */
export const LIFECYCLE_EVENTS_CLEANUP_TIME_BUDGET_MS = 1_500;

/**
 * Per-statement bound (`SET LOCAL statement_timeout`) on every DELETE and on
 * the oldest-row read, so one stalled statement cannot outlive the pass.
 */
export const LIFECYCLE_EVENTS_CLEANUP_STATEMENT_TIMEOUT_MS = 5_000;

export interface LifecycleEventsCleanupResponse {
  /** Rows deleted this call. */
  readonly deletedRows: number;
  /**
   * DELETE statements issued this call (0 when the budget or fuse admitted
   * none). A probe that deletes nothing is not counted.
   */
  readonly batches: number;
  /**
   * Age in days of the oldest surviving batch row. `null` when the table is
   * empty, or when the pass already exhausted the wall-clock budget
   * (the age query is skipped so jp + HTTP stay inside the control-loops
   * fetch timeout). Above `LIFECYCLE_EVENTS_RETENTION_DAYS` while draining;
   * the patrol reads it to tell "still catching up" from "the reaper stopped".
   */
  readonly oldestRemainingAgeDays: number | null;
  readonly errors: readonly string[];
  /**
   * Limits this call actually used (#2681): the contract defaults unless an
   * operator knob is set and valid. Additive; absent from older gateways.
   */
  readonly limits?: ReaperBatchLimits;
  /** Knobs this call IGNORED (invalid or above ceiling). Absent when none. */
  readonly configWarnings?: readonly string[];
}
